import React, { Fragment, useEffect, useState, useRef } from "react";
import { number, object, shape, string } from "prop-types";
import get from "lodash/get";
import throttle from "lodash/throttle";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "@quintype/components";

import { AuthorWithTime, CollectionName, FourColPortraitStories, Subheadline, ThreeColGrid } from "../../arrow";
import { HeroImage } from "../../arrow/components/Atoms/HeroImage";
import FourColGrid from "../../arrow/components/Rows/FourColGrid";
import { STATIC_KEYWORDS } from "../../constants";
import { Button, Headline } from "../atoms";
import { SvgIconHandler } from "../atoms/SvgIconHandler";
import { useDeviceType } from "../customHooks/useDeviceType";
import { STORY_HEADER_IN_VIEW } from "../store/actions";
import ListingAdSlot from "../molecules/ListingAdSlot";
import { getLoadMoreStories } from "../utils";
import { getCategoryAndSubCategory, getTemplateName } from "../../ga-utils";
import CircleIcon from "../atoms/Circle";
import { isDesktop, isMobile } from "react-device-detect";

import "./section.m.css";

const ElectionBanner = ({ desktopImage, mobileImage }) => {
  if (typeof window === "undefined") {
    // Avoid rendering on server at all
    return null;
  }

  return (
    <>
      {isDesktop && desktopImage && (
        <img
          height="232"
          width="1500"
          loading="lazy"
          alt="Bihar Elections 2025"
          title="Bihar Elections 2025"
          src={desktopImage}
        />
      )}
      {isMobile && mobileImage && (
        <img
          height="175"
          width="398"
          loading="lazy"
          alt="Bihar Elections 2025"
          title="Bihar Elections 2025"
          src={mobileImage}
        />
      )}
    </>
  );
};

const SectionPage = (props) => {
  const dispatch = useDispatch();
  const navRef = useRef(null);
  const qtConfig = useSelector((state) => get(state, ["qt", "config"], {}));
  const noStoriesFound =
    props.pageType === "collection-page" ? "No Collection Stories Found" : "No Section Stories Found";
  const isMagazineLandingPage = get(props, ["isMagazinePage"], false);
  const magazineTitle = get(props, ["magazineIssueTitle"], "");
  const childCollections = (get(props, ["data", "collection", "items"]) || []).filter(
    (item) => item.type === "collection" && item.items.length > 0
  );
  const isVisualStory = props?.data?.section?.slug === "visual-stories";
  const isAdfreePage = get(qtConfig, ["subscriber"]) === "true";

  const sectionCollection = get(props, ["data", "collection"], {});
  const parentSecStories = get(sectionCollection, ["items"], []).filter((item) => item?.type === "story");
  const desktopImage = get(sectionCollection, ["metadata", "desktop-image"], false);
  const mobileImage = get(sectionCollection, ["metadata", "mobile-image"], false);
  const maxStoriesLimit = props?.showAllStories ? parentSecStories.length : 9;
  const parentSectionFirstStory = get(parentSecStories, ["0", "story"], {});
  const parentSecAltHeadline = get(parentSectionFirstStory, ["alternative", "home", "default", "headline"], "");
  const { headline, authors, "published-at": publishedAt, slug } = parentSectionFirstStory;
  const secToNineParentStories =
    (isVisualStory ? parentSecStories?.slice(0, 4) : parentSecStories?.slice(1, maxStoriesLimit)) || [];
  const nineTo15ParentStories = isVisualStory
    ? parentSecStories?.slice(4, 12)
    : parentSecStories?.slice(maxStoriesLimit, isAdfreePage ? 15 : 14) || [];

  const [storiesToRender, setStoriesToRender] = useState(isVisualStory ? 12 : 15);
  const [otherParentSecStories, setOtherParentSecStories] = useState(nineTo15ParentStories);
  const [haveMoreStories, setHaveMoreStories] = useState(true);
  const storiesLoadLimit = isVisualStory ? 8 : 5;

  const { slug: sectionSlug, id: parentSectionId } = get(props, ["data", "section"], {});
  const isVideoSection = sectionSlug?.includes("videos");
  const isPhotoSection = sectionSlug?.includes("photos");
  const isVideosPhotosSection = isVideoSection || isPhotoSection;
  const newTitle = { videos: "All Videos News", photos: "All Photos News" };
  const parentSectionCollectionName = get(props, ["data", "collection", "name"], "");
  const qtState = useSelector((state) => get(state, ["qt"], {}));

  const newSectionTitle =
    newTitle[parentSectionCollectionName.toLowerCase()] || get(props, ["data", "collection", "name"], "");

  const allSections = get(props, ["config", "sections"], []).filter(
    (section) => section?.["parent-id"] === parentSectionId
  );

  const childParentSectionId = get(props, ["data", "section", "parent-id"], "");
  const childParentSection = get(props, ["config", "sections"], []).find(
    (section) => section?.id === childParentSectionId
  );

  const borderClass = isVideosPhotosSection ? "white-bottom-border" : "";
  const subHeadlineClass = isVideosPhotosSection ? "white-sub-headline" : "";

  // hard coded for now, will made dynamic in future sprints
  const premiumUser = false;
  const showDarkMode = sectionSlug?.toLowerCase() === "photos" || sectionSlug?.toLowerCase() === "videos";
  if (childParentSection) {
    const parentChildSections = get(props, ["config", "sections"], []).filter(
      (section) => section?.["parent-id"] === childParentSectionId
    );
    allSections.push(childParentSection, ...parentChildSections);
  } else {
    allSections.unshift(get(props, ["data", "section"], {}));
  }

  const { ALL, NEWS, LOAD_MORE } = STATIC_KEYWORDS;
  const deviceType = useDeviceType();
  const desktop = deviceType === "desktop";
  const currentPath = get(props, ["currentPath"], "");

  const qtStates = useSelector((state) => state);
  const storyHeaderInView = get(qtStates, "storyHeaderInView", true);

  const handleScroll = () => {
    if (window.scrollY > 200 && storyHeaderInView && desktop) {
      dispatch({
        type: STORY_HEADER_IN_VIEW,
        payload: false,
      });
    } else if (window.scrollY < 200 && !storyHeaderInView && desktop) {
      dispatch({
        type: STORY_HEADER_IN_VIEW,
        payload: true,
      });
    }
  };

  const throttledHandleScroll = throttle(handleScroll, 200);
  useEffect(() => {
    window.addEventListener("scroll", throttledHandleScroll);
    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
      throttledHandleScroll.cancel();
    };
  }, [throttledHandleScroll]);
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;

    if (el.scrollWidth > el.clientWidth) {
      el.style.justifyContent = "flex-start";
    } else {
      el.style.justifyContent = "center";
    }

    return () => {
      if (el) {
        el.style.justifyContent = "center";
      }
    };
  }, [navRef]);

  // Track Load More button clicks
  const trackLoadMoreClick = (sectionName) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, props.pageType);
      window.dataLayer.push({
        event: "dl_cta_click",
        link_category: sectionName?.toLowerCase() || parentSectionCollectionName?.toLowerCase(),
        link_text: "load more",
        category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: getTemplateName(props.pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  if (!parentSecStories.length) {
    return <h1>{noStoriesFound}</h1>;
  }

  const getMoreStories = async (
    offset,
    limit,
    slug,
    setStories,
    storiesToRender,
    setStoriesToRender,
    stories,
    setHaveMoreStories
  ) => {
    await getLoadMoreStories({
      offset: offset,
      limit: isAdfreePage && !isVisualStory ? limit : limit - 1,
      slug,
      shouldUseCollection: true,
      setStories,
      storiesToRender: storiesToRender,
      setStoriesToRender: setStoriesToRender,
      stories,
      isSectionPage: true,
      setHaveMoreStories,
    });
  };

  const ChildCollection = ({ slug, collection, pageType }) => {
    const [storiesToRender, setStoriesToRender] = useState(isAdfreePage ? 5 : 4);
    const sliceCount = isAdfreePage ? 5 : 4;
    const [childCollectionStories, setChildCollectionStories] = useState(collection?.items?.slice(0, sliceCount) || []);
    const [haveMoreStories, setHaveMoreStories] = useState(true);
    const storiesLoadLimit = 3;
    const childSectionSlug = props?.config?.sections?.filter((item) => item?.collection?.slug === slug)[0]?.slug;
    return (
      <div styleName="template-wrapper">
        <div styleName={`template-header ${isVideosPhotosSection ? "dark-headline" : ""}`}>
          <CollectionName collection={collection} />
          <Link href={`/${sectionSlug}/${childSectionSlug}`} styleName="link" disableAjaxLinks={true}>
            <span styleName="link-text">{STATIC_KEYWORDS.READ_MORE}</span>
            <SvgIconHandler type="arrow" width="24px" height="24px" viewBox="0 0 24 24" styleName="arrow-style" />
          </Link>
        </div>
        <FourColGrid
          collection={{ ...collection, items: childCollectionStories }}
          isLoadMoreVisible={false}
          showCollectionName={false}
          showReadTime={false}
          showMultipleAuthor={true}
          showAuthorContent={false}
          l2onl1page={true}
          pageType={pageType}
        />
        {!desktop && haveMoreStories && (
          <Button
            onClickFunction={() => {
              trackLoadMoreClick(collection?.name);
              getMoreStories(
                storiesToRender,
                storiesLoadLimit,
                slug,
                setChildCollectionStories,
                storiesToRender,
                setStoriesToRender,
                childCollectionStories,
                setHaveMoreStories
              );
            }}
          >
            {LOAD_MORE}
          </Button>
        )}
      </div>
    );
  };
  const activeSections = allSections?.filter((section) => !section?.data?.isDisabled);

  const dlCollection = { ...sectionCollection, items: secToNineParentStories };
  let dlTemplateName;
  dlTemplateName = getTemplateName(props?.pageType);
  const trackEvent = () => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, props?.pageType);
      window.dataLayer.push({
        event: "dl_story_listing_component_click",
        link_position: 1,
        section_name: dlCollection?.name ? dlCollection.name?.toLowerCase() : "NA",
        category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: dlTemplateName,
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  return (
    <div styleName={`section-page-wrapper ${showDarkMode ? "dark-mode" : ""} ${isVisualStory ? "visual" : ""}`}>
      <div
        className="container"
        styleName={`section-page ${activeSections?.length > 1 ? "" : "no-allsections-section-page"}`}
      >
        <div styleName="biharelectionbanner">
          <ElectionBanner desktopImage={desktopImage} mobileImage={mobileImage} />
        </div>
        {activeSections?.length > 1 && (
          <div styleName="section-header-wrapper">
            <div styleName="section-header" ref={navRef}>
              {allSections?.map((section, index) => {
                const { "section-url": sectionurl, id, name, "display-name": displayName, slug } = section;
                const isActive = slug === props.data.section.slug ? "active" : "";
                return section?.data?.isDisabled ? null : (
                  <Link href={`${sectionurl}`} key={id} styleName={`section-name ${isActive}`} disableAjaxLinks={true}>
                    {index === 0 ? ` ${displayName || name}` : displayName || name}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
        <h1 styleName="collection-name">
          <div>
            <CircleIcon width="24" height="24" />
          </div>
          <div>{isMagazineLandingPage ? magazineTitle : `${parentSectionCollectionName}`}</div>
        </h1>
        {!isVisualStory && (
          <div styleName="top-section">
            <div styleName={`left-section ${isAdfreePage || premiumUser ? "premium-user" : ""}`}>
              <div
                styleName={`${
                  isVideosPhotosSection
                    ? "story-content-wrapper dark-theme-section"
                    : " story-content-wrapper light-theme-section"
                }`}
              >
                <span style={{ cursor: "pointer" }} onClick={() => trackEvent()}>
                  <Link href={`/${slug}`} disableAjaxLinks={true}>
                    <Headline type={2} customStyleName="section-main-headline">
                      {parentSecAltHeadline || headline}
                    </Headline>
                  </Link>
                </span>

                <div styleName="story-content">
                  <AuthorWithTime story={parentSectionFirstStory} showAuthorContent={false} />
                </div>
                {(isAdfreePage || premiumUser) && (
                  <div styleName={`${isAdfreePage ? subHeadlineClass : ""}`}>
                    <Subheadline story={parentSectionFirstStory} />
                  </div>
                )}
              </div>
              <span style={{ cursor: "pointer" }} onClick={() => trackEvent()}>
                <HeroImage story={parentSectionFirstStory} aspectRatio={[[16, 9]]} />
              </span>
            </div>
            {!isAdfreePage && !premiumUser && (
              <ListingAdSlot adStyleName="ad-style-300x250" adType="right_side" pageType={props.pageType} />
            )}
          </div>
        )}
        <div styleName={`${isAdfreePage ? `top-template no-ad-top-template ${borderClass}` : "top-template"}`}>
          {isVisualStory ? (
            <FourColPortraitStories
              collection={{ ...sectionCollection, items: secToNineParentStories }}
              isLoadMoreVisible={false}
              showCollectionName={false}
              showReadTime={false}
              pageType={props?.pageType}
              fetchPriorityHigh={true}
            />
          ) : (
            <FourColGrid
              collection={{ ...sectionCollection, items: secToNineParentStories }}
              isLoadMoreVisible={false}
              showCollectionName={false}
              showReadTime={false}
              showAuthorContent={false}
              sectionSlug={props?.data?.section?.slug}
              pageType={props.pageType}
              adSlotConfigName={"mRec_ad"}
            />
          )}
          {otherParentSecStories.length > 0 && (
            <div styleName="template-wrapper">
              {!isVisualStory ? (
                <div styleName="template-header">
                  <h3 styleName="collection-name">
                    <CircleIcon />
                    {`All ${newSectionTitle
                      .replace(/^all\b\s*/i, "")
                      .replace(/\bnews\b$/i, "")
                      .trim()} News`}
                  </h3>
                </div>
              ) : null}
              <div styleName="last-template">
                {isVisualStory ? (
                  <FourColPortraitStories
                    collection={{ ...sectionCollection, items: otherParentSecStories }}
                    showReadTime={false}
                    pageType={props?.pageType}
                    indexPlusFour={true}
                  />
                ) : (
                  <ThreeColGrid
                    collection={{ ...sectionCollection, items: otherParentSecStories }}
                    showReadTime={false}
                    page={isVideoSection ? "video-listing" : isPhotoSection ? "photo-listing" : props?.pageType}
                    initialLoadCount={11}
                    adType="grid_ad"
                    showAuthorContent={false}
                  />
                )}
                {haveMoreStories && otherParentSecStories.length >= 4 && parentSecStories.length > 13 && (
                  <Button
                    onClickFunction={() => {
                      trackLoadMoreClick();
                      getMoreStories(
                        storiesToRender,
                        storiesLoadLimit,
                        get(props, ["data", "collection", "slug"], ""),
                        setOtherParentSecStories,
                        storiesToRender,
                        setStoriesToRender,
                        otherParentSecStories,
                        setHaveMoreStories
                      );
                    }}
                  >
                    {LOAD_MORE}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
        {!isAdfreePage && !isVisualStory && (
          <ListingAdSlot
            adStyleName="ad-slot-size-728x90-300x250"
            adType="below_top_listing"
            pageType={isPhotoSection ? "photo-listing" : isVideoSection ? "video-listing" : props.pageType}
            index={5}
          />
        )}
        {childCollections?.map((collection, index) => {
          const { name, slug } = collection;
          return (
            <Fragment key={collection?.id}>
              <ChildCollection name={name} slug={slug} collection={collection} pageType={props.pageType} />
              {!isAdfreePage && !premiumUser && desktop && index < 3 && (
                <div styleName="ad-container middle-ad">
                  <ListingAdSlot
                    adStyleName="ad-slot-size-728x90-300x250"
                    adType="collection_ad"
                    index={index}
                    pageType={props.pageType}
                  />
                </div>
              )}
            </Fragment>
          );
        })}
        {!isAdfreePage && !desktop && childCollections?.length ? (
          <ListingAdSlot
            adStyleName="ad-slot-size-300x250"
            adType="last_listing"
            index={2}
            pageType={props.pageType}
            adSlotConfigName={"mRec_ad"}
          />
        ) : null}
        {otherParentSecStories.length > 0 && (
          <div styleName="template-wrapper">
            {!isVisualStory ? (
              <div styleName="template-header">
                <h3 styleName="collection-name">
                  <CircleIcon />
                  {`All ${newSectionTitle
                    .replace(/^all\b\s*/i, "")
                    .replace(/\bnews\b$/i, "")
                    .trim()} News`}
                </h3>
              </div>
            ) : null}
            <div styleName="last-template">
              {isVisualStory ? (
                <FourColPortraitStories
                  collection={{ ...sectionCollection, items: otherParentSecStories }}
                  showReadTime={false}
                  pageType={props?.pageType}
                  indexPlusFour={true}
                />
              ) : (
                <ThreeColGrid
                  collection={{ ...sectionCollection, items: otherParentSecStories }}
                  showReadTime={false}
                  page={isVideoSection ? "video-listing" : isPhotoSection ? "photo-listing" : props?.pageType}
                  initialLoadCount={11}
                  adType="grid_ad"
                  showAuthorContent={false}
                />
              )}
              {haveMoreStories &&
                otherParentSecStories.length >= (isAdfreePage ? 5 : 4) &&
                parentSecStories.length > (isAdfreePage ? 14 : 13) && (
                  <Button
                    onClickFunction={() => {
                      trackLoadMoreClick();
                      getMoreStories(
                        storiesToRender,
                        storiesLoadLimit,
                        get(props, ["data", "collection", "slug"], ""),
                        setOtherParentSecStories,
                        storiesToRender,
                        setStoriesToRender,
                        otherParentSecStories,
                        setHaveMoreStories
                      );
                    }}
                  >
                    {LOAD_MORE}
                  </Button>
                )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

SectionPage.propTypes = {
  pageType: string,
  data: shape({
    collection: object,
    section: string,
  }),
};

export { SectionPage };
